import {
    Badge,
    Box,
    Container,
    Flex,
    Heading,
    HStack,
    Icon,
    SimpleGrid,
    Stack,
    Text,
} from '@chakra-ui/react';
import type { IconType } from 'react-icons';
import {
    LuBuilding2,
    LuCalendarCheck,
    LuCheck,
    LuClock,
    LuFilePlus,
    LuFiles,
    LuUsers,
} from 'react-icons/lu';
import { useI18n } from 'src/i18n/I18nContext';

const fitCriteria = ['projects', 'funds', 'partners', 'currencies', 'reconciliation'] as const;

interface OperationDefinition {
    key: 'timesheets' | 'absences' | 'organization' | 'documents';
    icon: IconType;
}

const operations: OperationDefinition[] = [
    { key: 'timesheets', icon: LuClock },
    { key: 'absences', icon: LuCalendarCheck },
    { key: 'organization', icon: LuUsers },
    { key: 'documents', icon: LuFilePlus },
];

const CustomerFit = () => {
    const { t } = useI18n();

    return (
        <Box
            id='for-ngos'
            as='section'
            py={{ base: 12, md: 20 }}
            bg='bg.subtle'
            borderYWidth='1px'
            borderColor='border.subtle'
            scrollMarginTop='5rem'
            data-testid='customer-fit'
        >
            <Container maxW={{ base: '2xl', sm: '3xl', md: '4xl', lg: '6xl' }}>
                <Stack align='center' gap='3' mb={{ base: 8, md: 12 }} textAlign='center'>
                    <Text color='blue.fg' fontSize='sm' fontWeight='bold' letterSpacing='wide' textTransform='uppercase'>
                        {t('customerFit.eyebrow')}
                    </Text>
                    <Heading maxW='4xl' fontSize={{ base: '3xl', md: '4xl' }} lineHeight='1.15'>
                        {t('customerFit.title')}
                    </Heading>
                    <Text maxW='3xl' color='fg.muted' fontSize={{ base: 'md', md: 'lg' }} lineHeight='1.7'>
                        {t('customerFit.intro')}
                    </Text>
                </Stack>

                <Stack
                    maxW='4xl'
                    mx='auto'
                    gap='6'
                    p={{ base: 5, md: 7 }}
                    rounded='2xl'
                    borderWidth='1px'
                    borderColor='border.subtle'
                    bg='bg.panel'
                    boxShadow='md'
                >
                    <Flex align='flex-start' justify='space-between' gap='4' flexWrap='wrap'>
                        <HStack gap='3'>
                            <Flex boxSize='11' align='center' justify='center' rounded='xl' bg='blue.subtle' color='blue.fg'>
                                <Icon as={LuBuilding2} boxSize='6' />
                            </Flex>
                            <Heading as='h3' fontSize='2xl'>{t('customerFit.builtFor.title')}</Heading>
                        </HStack>
                        <Badge colorPalette='blue' variant='subtle' rounded='full' px='3' py='1'>
                            {t('customerFit.builtFor.size')}
                        </Badge>
                    </Flex>
                    <Stack as='ul' listStyleType='none' gap='3'>
                        {fitCriteria.map((criterion) => (
                            <HStack
                                as='li'
                                data-testid='fit-criterion'
                                key={criterion}
                                align='flex-start'
                                gap='3'
                            >
                                <Flex boxSize='6' mt='0.5' flexShrink={0} align='center' justify='center' rounded='full' bg='teal.subtle' color='teal.fg'>
                                    <Icon as={LuCheck} boxSize='4' />
                                </Flex>
                                <Text lineHeight='1.55'>{t(`customerFit.builtFor.criteria.${criterion}`)}</Text>
                            </HStack>
                        ))}
                    </Stack>
                    <HStack align='flex-start' gap='3' pt='5' borderTopWidth='1px' borderColor='border.subtle'>
                        <Flex boxSize='8' flexShrink={0} align='center' justify='center' rounded='lg' bg='blue.subtle' color='blue.fg'>
                            <Icon as={LuFiles} />
                        </Flex>
                        <Stack gap='0.5'>
                            <Text fontSize='sm' fontWeight='bold'>{t('customerFit.builtFor.exchangeTitle')}</Text>
                            <Text color='fg.muted' fontSize='sm' lineHeight='1.6'>{t('customerFit.builtFor.exchangeText')}</Text>
                        </Stack>
                    </HStack>
                </Stack>

                <Stack mt={{ base: 10, md: 14 }} gap='6'>
                    <Stack gap='2' maxW='3xl'>
                        <Text color='fg.subtle' fontSize='sm' fontWeight='bold' letterSpacing='wide' textTransform='uppercase'>
                            {t('customerFit.operations.eyebrow')}
                        </Text>
                        <Heading as='h3' fontSize={{ base: '2xl', md: '3xl' }}>{t('customerFit.operations.title')}</Heading>
                        <Text color='fg.muted' lineHeight='1.7'>{t('customerFit.operations.intro')}</Text>
                    </Stack>

                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap='4'>
                        {operations.map((operation) => (
                            <Stack
                                key={operation.key}
                                data-testid='operation-card'
                                gap='3'
                                p='4'
                                rounded='xl'
                                borderWidth='1px'
                                borderColor='border.subtle'
                                bg='bg.panel'
                            >
                                <Flex boxSize='9' align='center' justify='center' rounded='lg' bg='bg.muted' color='fg.muted'>
                                    <Icon as={operation.icon} boxSize='5' />
                                </Flex>
                                <Stack gap='1.5'>
                                    <Text fontWeight='bold'>{t(`customerFit.operations.items.${operation.key}.title`)}</Text>
                                    <Text color='fg.muted' fontSize='sm' lineHeight='1.55'>
                                        {t(`customerFit.operations.items.${operation.key}.text`)}
                                    </Text>
                                </Stack>
                            </Stack>
                        ))}
                    </SimpleGrid>
                </Stack>
            </Container>
        </Box>
    );
};

export default CustomerFit;
